import { api } from '../../lib/api.ts';

/** Reads an axios error whose body is a Blob (responseType 'blob') back into JSON. */
async function readBlobError(data: unknown): Promise<{ error?: string; file?: string } | null> {
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text()) as { error?: string; file?: string };
    } catch {
      return null;
    }
  }
  return null;
}

export interface MergeFailure {
  code: string;
  file?: string;
}

function triggerDownload(blob: Blob, filename: string): void {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

export const mergeApi = {
  /**
   * Merge the given files (in array order) into one PDF on the server and trigger
   * a browser download. Throws a MergeFailure with the server's error code on 4xx.
   */
  merge: async (files: File[], filename: string, compress = false): Promise<void> => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    fd.append('filename', filename);
    if (compress) fd.append('compress', 'true');
    try {
      const r = await api.post<Blob>('/merge', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob',
      });
      triggerDownload(r.data, filename);
    } catch (err) {
      const resp = (err as { response?: { data?: unknown } }).response;
      const parsed = await readBlobError(resp?.data);
      const failure: MergeFailure = { code: parsed?.error ?? 'merge_failed', file: parsed?.file };
      throw failure;
    }
  },
};
